import React, { useEffect, useState } from 'react';
import { ILineAmendmentLovs, ILineAmendmentProps } from '.';
import AmendmentDetails from '../components/AmendmentDetails';
import PolicyCovers from '../components/PolicyCovers';
import CostsAndCharges from '../components/CostsAndCharges';
import MotorDetails from '../components/MotorDetails';
import { useLazyQuery } from '@apollo/client';
import { getCoversPersonsQuery, getMotorDetailsQuery } from '../queries';
import {
  convertToPolicyPlanProposalCovers,
  mapToAmendmentDetails,
} from '../utils';
import { cloneDeep } from 'lodash';
import { initialCoverValues } from '../components/content';
import Loader from '@components/loader/Loader';
import InsuredWidget from '../components/policy-persons-widgets/InsuredWidget';
import { getAmendmentInitialState } from '../content';
import { convertToPolicyPersons } from '../components/policy-persons-widgets/utils';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';
import AmendmentPolicyDetails from '../components/AmendmentPolicyDetails';

const MotorAmendment: React.FC<ILineAmendmentProps> = ({
  generalData,
  data,
  pageState,
  handleStateChange,
}) => {
  const amendmentData = data?.Production?.queries?.getAmendmentDetails;
  const isGroupPolicy = generalData.policy.type === 'GROUP';
  const [loading, setLoading] = useState<boolean>();
  const isAmendmentClosed = ['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(
    generalData.amendment.status
  );

  const [lovs, setLovs] = useState<ILineAmendmentLovs>({
    amendmentTypes: {},
    amendmentSubTypes: {},
  });

  const [getCoversPersonsLazy] = useLazyQuery(getCoversPersonsQuery('both'));

  const [getMotorDetailsLazy] = useLazyQuery(getMotorDetailsQuery, {
    variables: {
      selectedAmendmentID: generalData.amendment.id,
      version: generalData.policy.version,
    },
    errorPolicy: 'all',
  });

  const initialize = async () => {
    setLoading(true);

    const { data: detailsData } = await getMotorDetailsLazy();

    const newValues = mapToAmendmentDetails({
      ...amendmentData?.[0],
      ...detailsData.Production.queries.getAmendmentDetails[0],
    });

    const newErrors = cloneDeep(
      getAmendmentInitialState(generalData.line.category).errors
    );
    const newTouched = cloneDeep(
      getAmendmentInitialState(generalData.line.category).touched
    );

    // Get covers and/or persons – start
    const { data: coversPersonsData } = await getCoversPersonsLazy({
      variables: {
        id: generalData.proposal.id,
        version: generalData.policy.version,
      },
      errorPolicy: 'all',
    });
    const covers = convertToPolicyPlanProposalCovers(
      coversPersonsData,
      amendmentData
    );
    newValues.covers = covers;
    newErrors.covers = covers.map(() => initialCoverValues.initialError);
    newTouched.covers = covers.map(() => initialCoverValues.initialTouched);

    const persons = convertToPolicyPersons(coversPersonsData);
    newValues.policyPersons = persons;
    newErrors.policyPersons = {
      insured: persons.insured.map(() => newErrors['policyPersons'].insured[0]),
    };
    newTouched.policyPersons = {
      insured: persons.insured.map(
        () => newTouched['policyPersons'].insured[0]
      ),
    };
    // Get covers and/or persons – end

    setLovs({
      amendmentTypes: enumListAsRecordObject(
        data.Production_AmendmentTypes.enumValues
      ),
      amendmentSubTypes: lookupListAsRecordObject(
        data.Production.lookups.amendmentSubTypes,
        false,
        'Code',
        'Title'
      ),
    });

    handleStateChange({
      values: newValues,
      errors: newErrors,
      touched: newTouched,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (data) {
      initialize();
    }
  }, [data]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <AmendmentDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isAmendmentClosed}
      />
      <AmendmentPolicyDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isAmendmentClosed}
      />
      <PolicyCovers
        generalData={generalData}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isAmendmentClosed}
        isGroupPolicy={isGroupPolicy}
      />
      <CostsAndCharges
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isAmendmentClosed}
      />
      <InsuredWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isAmendmentClosed}
        maxNumber={
          amendmentData?.[0]?.policy_PlanID?.views?.PlanConfigManagement_all
            ?.properties?.MaxNbInsured
        }
      />
      {!isGroupPolicy && (
        <MotorDetails
          generalData={generalData}
          pageState={pageState}
          onPageStateUpdate={handleStateChange}
          isDisabled={isAmendmentClosed}
        />
      )}
    </>
  );
};

export default MotorAmendment;
